import { useState } from 'react';
import { PageLayout } from '@/shared/layout/PageLayout';
import { useResources } from './hooks/useResources';
import { permissionEngine } from '@/core/permissions';
import { useAuth } from '@/core/auth/AuthContext';
import { AuthButton } from '@/core/permissions/AuthButton';
import { Plus, Edit2, Trash2, HardHat } from 'lucide-react';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { ResourceModal } from './components/ResourceModal';
import type { EstimateResource } from './types/resources.types';

export function EstimateResourcesPage() {
  const { profile } = useAuth();
  permissionEngine.assert(profile?.id || '', 'inventory.resources.view');

  const { data, isLoading, deleteResource, isDeleting } = useResources();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<EstimateResource | undefined>();
  const [deleteTarget, setDeleteTarget] = useState<EstimateResource | null>(null);

  const handleEdit = (item: EstimateResource) => {
    setEditingItem(item);
    setModalOpen(true);
  };

  const handleAdd = () => {
    setEditingItem(undefined);
    setModalOpen(true);
  };

  const handleDelete = async () => {
    if (deleteTarget) {
      await deleteResource(deleteTarget.id);
      setDeleteTarget(null);
    }
  };

  return (
    <PageLayout title="Danh mục tài nguyên dự toán" description="Quản lý các loại máy móc, thiết bị, vật tư, nhiên liệu dùng trong định mức">
      <div className="flex flex-col h-full gap-4">
        <div className="flex items-center justify-between">
          <h3 className="text-lg font-semibold text-slate-800">Danh sách tài nguyên</h3>
          <AuthButton
            module="inventory.resources"
            action="create"
            behavior="disable"
            className="bg-emerald-600 hover:bg-emerald-700 text-white h-9 px-4 rounded-md inline-flex items-center gap-2 sm:text-sm text-xs"
            onClick={handleAdd}
          >
            <Plus className="w-4 h-4" />
            <span className="hidden sm:inline">Thêm tài nguyên</span>
          </AuthButton>
        </div>

        <div className="flex-1 bg-white border border-slate-200 rounded-md overflow-hidden min-h-0 flex flex-col">
          <div className="flex-1 overflow-auto min-h-0 relative">
            <table className="w-full text-left border-collapse">
              <thead className="bg-slate-50 sticky top-0 z-10 border-b border-slate-200">
                <tr>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700">Mã & Tên tài nguyên</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700">Nhóm / Danh mục</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-24">Đơn vị</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-32">Trạng thái</th>
                  <th className="px-4 py-3 sm:text-sm text-xs font-semibold text-slate-700 w-24 text-right">Thao tác</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200 bg-white">
                {isLoading ? (
                  <tr>
                    <td colSpan={5} className="p-8 text-center text-slate-500 animate-pulse">Đang tải dữ liệu...</td>
                  </tr>
                ) : data.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-12 text-center text-slate-500">
                      <HardHat className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                      Chưa có tài nguyên nào được thiết lập.
                    </td>
                  </tr>
                ) : (
                  data.map((item) => (
                    <tr key={item.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3">
                        <div className="font-medium text-slate-900 sm:text-sm text-xs">{item.name}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5 font-mono">{item.code || '-'}</div>
                      </td>
                      <td className="px-4 py-3">
                        <div className="sm:text-sm text-xs text-slate-700">{item.category?.name || 'Chưa phân nhóm'}</div>
                        <div className="text-[11px] text-slate-500 mt-0.5">{item.category?.code || ''}</div>
                      </td>
                      <td className="px-4 py-3 sm:text-sm text-xs text-slate-700">
                        {item.unit || '-'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-medium ${
                          item.status === 'active' ? 'bg-emerald-100 text-emerald-800' : 'bg-slate-100 text-slate-800'
                        }`}>
                          {item.status === 'active' ? 'Sẵn sàng' : 'Vô hiệu hóa'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-right space-x-2">
                        <AuthButton
                          module="inventory.resources"
                          action="update"
                          behavior="hide"
                          type="button"
                          onClick={() => handleEdit(item)}
                          className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </AuthButton>
                        <AuthButton
                          module="inventory.resources"
                          action="delete"
                          behavior="hide"
                          type="button"
                          onClick={() => setDeleteTarget(item)}
                          className="p-1.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 rounded transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </AuthButton>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <ResourceModal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        resource={editingItem}
      />

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Xác nhận xóa tài nguyên"
        message={
          <span>
            Bạn có chắc chắn muốn xóa tài nguyên <strong>{deleteTarget?.name}</strong>?
          </span>
        }
        confirmText="Xóa"
        cancelText="Hủy"
        isDestructive
        isLoading={isDeleting}
        onConfirm={handleDelete}
        onClose={() => setDeleteTarget(null)}
      />
    </PageLayout>
  );
}

export default EstimateResourcesPage;
